import { Descendant, Element, Text } from 'slate';

import { CustomText, CustomElement, ParagraphElement } from '@v2/elements/RichTextEditor/types/slate';
import { convertRgbToHex } from '@v2/elements/RichTextEditor/config';

export function rtfToSlate(rtfContent: string): Descendant[] {
  const parser = new RTFParser(rtfContent);
  const nodes = parser.parse();
  return nodes;
}

class RTFParser {
  private content: string;
  private position: number = 0;
  private nodes: Descendant[] = [];
  private currentFormatting: Partial<CustomText> = {};
  private currentAlignment: string | null = null;
  private colorTable: Map<number, string> = new Map();
  private currentListType: 'bulleted' | 'numbered' | null = null;
  private isInListItem: boolean = false;
  private listItemBuffer: string = '';

  constructor(rtfContent: string) {
    this.content = rtfContent;
  }

  parse(): Descendant[] {
    // skip header
    this.skipToContent();

    while (this.position < this.content.length && !this.isAtEnd()) {
      this.parseNode();
    }

    const nodes = this.normalizeSlateNodes(this.nodes);
    if (nodes.length === 0) {
      return [
        {
          type: 'paragraph',
          children: [{ text: '' }],
        },
      ];
    }

    return nodes;
  }

  private skipToContent(): void {
    // Skip rtf header tables
    this.parseColorTable();

    ['fonttbl', 'colortbl', 'stylesheet', 'listtable', 'listoverridetable'].forEach((tableName) => this.skipTable(tableName));

    // todo invalid indicator
    const contentStartIndicator = '\\slmult1';
    const contentStart = this.content.indexOf(contentStartIndicator);

    if (contentStart !== -1) {
      this.position = contentStart + contentStartIndicator.length;
    }
  }

  private parseColorTable(): void {
    this.content = this.content.replace(/\{\\colortbl\s?;/g, '{\\colortbl;');
    const start = this.content.indexOf(`{\\colortbl;`);

    if (start === -1) {
        return;
    }

    let braceCount = 1;
    let i = start + 9;

    do {
      if (this.content[i] === '{') braceCount++;
      if (this.content[i] === '}') braceCount--;
      i++;
    } while (braceCount > 0 && i < this.content.length);

    const colorTable = this.content.substring(start, i);

    // In RTF, \cf0 means default/auto color
    // The color table entries correspond to \cf1, \cf2, etc.
    let colorIndex = 1;
    colorTable.split(';').forEach((color) => {
      const match = /\\red(\d+)\\green(\d+)\\blue(\d+)/.exec(color);

      if (!match) {
        return;
      }

      const red = match[1];
      const green = match[2];
      const blue = match[3];

      const hexFormat = convertRgbToHex(+red, +green, +blue);

      this.colorTable.set(colorIndex, hexFormat);
      colorIndex++;
    });
  }

  private skipTable(tableHeader: string): void {
    const start = this.content.indexOf(`{\\${tableHeader}`);
    if (start === -1) return;

    let braceCount = 1;
    let i = start + 9;

    do {
      if (this.content[i] === '{') braceCount++;
      if (this.content[i] === '}') braceCount--;
      i++;
    } while (braceCount > 0 && i < this.content.length);

    this.content = this.content.substring(i, this.content.length + 1);
  }

  private parseNode(): void {
    this.skipWhitespace();

    if (this.peek() === '\\') {
      this.parseControlWord();
    } else if (this.peek() === '{') {
      this.parseGroup();
    } else if (this.peek() === '}') {
      this.advance(); // Skip closing brace
    } else {
      this.parseText();
    }
  }

  private parseControlWord(): void {
    if (!this.match('\\')) return;

    const controlWord = this.readControlWord();
    const parameter = this.readParameter();

    this.handleControlWord(controlWord, parameter);
  }

  private readControlWord(): string {
    let word = '';
    while (this.position < this.content.length && /[a-zA-Z]/.test(this.peek())) {
      word += this.advance();
    }
    return word;
  }

  private readParameter(): number | null {
    let param = '';
    let hasNegative = false;

    const nextValue = this.peek();
    if (nextValue === '-') {
      hasNegative = true;
      this.advance();
    }

    while (this.position < this.content.length && /\d/.test(this.peek())) {
      param += this.advance();
    }

    if (param === '') return null;

    const value = parseInt(param, 10);
    return hasNegative ? -value : value;
  }

  private handleControlWord(word: string, parameter: number | null): void {
    switch (word) {
      case 'b':
        this.currentFormatting.bold = parameter !== 0;
        break;
      case 'i':
        this.currentFormatting.italic = parameter !== 0;
        break;
      case 'ul':
        this.currentFormatting.underline = parameter !== 0;
        break;
      case 'cf':
        if (parameter) {
          const color = this.colorTable.get(parameter);
          this.currentFormatting.color = color;
        } else {
          this.currentFormatting.color = undefined;
        }
        break;
      case 'strike':
        this.currentFormatting.strikethrough = parameter !== 0;
        break;
      case 'fs':
        // font size - preserve it in currentFormatting
        if (parameter) {
          this.currentFormatting.fontSize = parameter;
          // Only create heading for large sizes
          if (parameter > 30) {
            this.createHeadingFromFontSize(parameter);
          }
        } else {
          // Reset font size when fs0 is encountered
          delete this.currentFormatting.fontSize;
        }
        break;
      case 'par':
        if (this.isInListItem) {
          this.processListItem();
          this.isInListItem = false;
          this.listItemBuffer = '';
        } else {
          this.finalizeParagraph();
        }
        break;
      case 'line':
        this.addLineBreak();
        break;
      case 'qc':
        this.currentAlignment = 'center';
        this.applyAlignmentToCurrentParagraph();
        break;
      case 'qr':
        this.currentAlignment = 'right';
        this.applyAlignmentToCurrentParagraph();
        break;
      case 'qj':
        this.currentAlignment = 'justify';
        this.applyAlignmentToCurrentParagraph();
        break;
      case 'ql':
        this.currentAlignment = 'left';
        this.applyAlignmentToCurrentParagraph();
        break;
      case 'pard':
        // Reset paragraph formatting to defaults
        this.currentAlignment = null;
        break;
      case 'bullet':
        this.createBulletPoint();
        break;
      case 'li':
        // List indentation - might indicate we're in a list
        this.isInListItem = true;
        this.listItemBuffer = '';
        break;
      case 'tab':
        if (this.isInListItem) {
          // In list context, tab separates the bullet/number from content
          this.listItemBuffer += '\t';
        } else {
          this.addText('\t');
        }
        break;
    }
  }

  private parseGroup(): void {
    if (!this.match('{')) return;

    const savedFormatting = { ...this.currentFormatting };
    const savedAlignment = this.currentAlignment;

    while (this.position < this.content.length && this.peek() !== '}') {
      this.parseNode();
    }

    this.currentFormatting = savedFormatting;
    this.currentAlignment = savedAlignment;

    this.match('}');
  }

  private parseText(): void {
    let text = '';

    while (this.position < this.content.length && this.peek() !== '\\' && this.peek() !== '{' && this.peek() !== '}') {
      text += this.advance();
    }

    if (text.trim()) {
      const unescapedText = this.unescapeRTFText(text);

      if (this.isInListItem) {
        // Accumulate text for the list item
        this.listItemBuffer += unescapedText;
      } else {
        // Check if this looks like a numbered list item (e.g., "1. ", "2. ", etc.)
        const numberedMatch = /^(\d+)\.\s+(.*)/.exec(unescapedText);
        if (numberedMatch) {
          // Create or continue a numbered list
          this.createNumberedListItem(numberedMatch[2]);
        } else {
          this.addText(unescapedText);
        }
      }
    }
  }

  private addText(text: string): void {
    if (!text.trim()) {
      return;
    }

    // If we're in a list item context, accumulate the text
    if (this.isInListItem) {
      this.listItemBuffer += text;
      return;
    }

    const textNode: CustomText = {
      text,
      ...this.currentFormatting,
    };

    const lastNode = this.nodes[this.nodes.length - 1];

    if (lastNode && Element.isElement(lastNode) && lastNode.type === 'bulleted-list') {
      lastNode.children.push({ type: 'list-item', children: [textNode] });
    } else if (lastNode && Element.isElement(lastNode) && lastNode.type === 'numbered-list') {
      // If we're in a numbered list and getting more text, add it to the last list item
      const lastItem = lastNode.children[lastNode.children.length - 1];
      if (lastItem && Element.isElement(lastItem) && lastItem.type === 'list-item') {
        lastItem.children.push(textNode);
      }
    } else {
      let currentParagraph = this.getCurrentParagraph();

      if (!currentParagraph) {
        currentParagraph = this.createNewParagraph();
      }

      currentParagraph.children.push(textNode);
    }
  }

  private getCurrentBulletList(): CustomElement | null {
    const lastNode = this.nodes[this.nodes.length - 1];
    if (lastNode && Element.isElement(lastNode) && lastNode.type === 'bulleted-list') {
      return lastNode as CustomElement;
    }

    return null;
  }

  private getCurrentParagraph(): ParagraphElement | null {
    const lastNode = this.nodes[this.nodes.length - 1];
    if (lastNode && Element.isElement(lastNode) && (lastNode.type === 'paragraph' || lastNode.type.includes('heading'))) {
      return lastNode as ParagraphElement;
    }
    return null;
  }

  private createNewParagraph(): ParagraphElement {
    const paragraph: ParagraphElement = {
      type: 'paragraph',
      children: [],
      ...(this.currentAlignment && { align: this.currentAlignment }),
    };

    this.nodes.push(paragraph);
    return paragraph;
  }

  private applyAlignmentToCurrentParagraph(): void {
    let currentParagraph = this.getCurrentParagraph();
    if (!currentParagraph) {
      // If no current paragraph exists, create one
      currentParagraph = this.createNewParagraph();
    }

    if (this.currentAlignment) {
      currentParagraph.align = this.currentAlignment;
    } else {
      delete currentParagraph.align;
    }
  }

  private finalizeParagraph(): void {
    let lastNode = this.nodes[this.nodes.length - 1];

    // If we're in a list, don't create a new paragraph
    if (lastNode && Element.isElement(lastNode) && (lastNode.type === 'bulleted-list' || lastNode.type === 'numbered-list')) {
      return;
    }

    let currentParagraph = this.getCurrentParagraph();

    if (!currentParagraph) {
      currentParagraph = this.createNewParagraph();
    }

    if (currentParagraph && currentParagraph.children.length === 0) {
      currentParagraph.children.push({ text: '' });
    }

    // Create a new paragraph for the next content
    this.createNewParagraph();
    // Reset alignment after paragraph - RTF alignment doesn't carry forward by default
    this.currentAlignment = null;
    // Reset list type when we're done with the list
    this.currentListType = null;
  }

  private addLineBreak(): void {
    this.addText('\n');
  }

  private createHeadingFromFontSize(fontSize: number): void {
    let headingType: string;

    if (fontSize >= 36) headingType = 'heading-one';
    else if (fontSize >= 32) headingType = 'heading-two';
    else if (fontSize >= 28) headingType = 'heading-three';
    else if (fontSize >= 26) headingType = 'heading-four';
    else if (fontSize >= 24) headingType = 'heading-five';
    else headingType = 'heading-six';

    const lastNode = this.nodes[this.nodes.length - 1];
    if (lastNode && Element.isElement(lastNode) && lastNode.type === 'paragraph') {
      (lastNode as any).type = headingType;
    }
  }

  private createBulletPoint(): void {
    const currentList = this.getCurrentBulletList();

    if (!currentList) {
      const listItem: CustomElement = {
        type: 'bulleted-list',
        children: [],
      };
      this.nodes.push(listItem);
      this.currentListType = 'bulleted';
    }
  }

  private createNumberedListItem(text: string): void {
    const lastNode = this.nodes[this.nodes.length - 1];

    // Check if we're already in a numbered list
    if (lastNode && Element.isElement(lastNode) && lastNode.type === 'numbered-list') {
      // Add to existing numbered list
      lastNode.children.push({
        type: 'list-item',
        children: [{ text: text.trim() }]
      });
    } else {
      // Create a new numbered list
      const numberedList: CustomElement = {
        type: 'numbered-list',
        children: [{
          type: 'list-item',
          children: [{ text: text.trim() }]
        }],
      };
      this.nodes.push(numberedList);
      this.currentListType = 'numbered';
    }
  }

  private processListItem(): void {

    // Clean up the buffer - handle tabs
    const cleanBuffer = this.listItemBuffer.replace(/\t/g, ' ');

    // Check if it's a numbered list item (look for patterns like "1." or "1. ")
    const numberedMatch = /^(\d+)\.(\s|$)/.exec(cleanBuffer.trim());
    const bulletMatch = cleanBuffer.includes('bullet') || cleanBuffer.trim().startsWith('•');

    if (numberedMatch) {
      // Remove the number, period, and any following spaces/tabs
      const text = cleanBuffer.trim().replace(/^\d+\.\s*/, '').trim();

      const lastNode = this.nodes[this.nodes.length - 1];
      if (lastNode && Element.isElement(lastNode) && lastNode.type === 'numbered-list') {
        // Add to existing numbered list
        lastNode.children.push({
          type: 'list-item',
          children: text ? [{ text: text, ...this.currentFormatting }] : [{ text: '' }]
        });
      } else {
        // Create new numbered list
        const numberedList: CustomElement = {
          type: 'numbered-list',
          children: [{
            type: 'list-item',
            children: text ? [{ text: text, ...this.currentFormatting }] : [{ text: '' }]
          }],
        };
        this.nodes.push(numberedList);
      }
    } else if (bulletMatch) {
      // Remove bullet and any following spaces/tabs
      const text = cleanBuffer.replace(/bullet/g, '').replace(/^[•·]\s*/, '').trim();

      const lastNode = this.nodes[this.nodes.length - 1];
      if (lastNode && Element.isElement(lastNode) && lastNode.type === 'bulleted-list') {
        // Add to existing bulleted list
        lastNode.children.push({
          type: 'list-item',
          children: text ? [{ text: text, ...this.currentFormatting }] : [{ text: '' }]
        });
      } else {
        // Create new bulleted list
        const bulletedList: CustomElement = {
          type: 'bulleted-list',
          children: [{
            type: 'list-item',
            children: text ? [{ text: text, ...this.currentFormatting }] : [{ text: '' }]
          }],
        };
        this.nodes.push(bulletedList);
      }
    } else if (this.listItemBuffer.trim()) {
      // Regular text with indentation, might be part of a list without clear markers

      // Check if we're continuing a list
      const lastNode = this.nodes[this.nodes.length - 1];
      if (lastNode && Element.isElement(lastNode) && (lastNode.type === 'numbered-list' || lastNode.type === 'bulleted-list')) {
        // Continue the existing list
        lastNode.children.push({
          type: 'list-item',
          children: [{ text: cleanBuffer.trim(), ...this.currentFormatting }]
        });
      } else {
        // Not in list context, treat as regular text
        const para = this.createNewParagraph();
        para.children.push({ text: cleanBuffer.trim(), ...this.currentFormatting });
      }
    }
  }

  private getCurrentNumberedList(): CustomElement | null {
    const lastNode = this.nodes[this.nodes.length - 1];
    if (lastNode && Element.isElement(lastNode) && lastNode.type === 'numbered-list') {
      return lastNode as CustomElement;
    }
    return null;
  }

  private unescapeRTFText(text: string): string {
    return text
      .replace(/\\tab /g, '\t')
      .replace(/\\line /g, '\n')
      .replace(/\\\\/g, '\\')
      .replace(/\\{/g, '{')
      .replace(/\\}/g, '}')
      .trim();
  }

  private peek(): string {
    return this.content[this.position] || '';
  }

  private advance(): string {
    return this.content[this.position++] || '';
  }

  private match(expected: string): boolean {
    if (this.peek() === expected) {
      this.advance();
      return true;
    }
    return false;
  }

  private skipWhitespace(): void {
    while (this.position < this.content.length && /\s/.test(this.peek())) {
      this.advance();
    }
  }

  private isAtEnd(): boolean {
    const remaining = this.content.substring(this.position).trim();
    return remaining === '}' || remaining === '';
  }

  private normalizeSlateNodes(nodes: Descendant[], root = true): Descendant[] {
    const normalized: Descendant[] = [];

    for (const node of nodes) {
      if (Element.isElement(node)) {
        // ensure all elements have children
        if (!node.children || node.children.length === 0) {
          (node as CustomElement).children = [{ text: '' }];
        }

        (node as CustomElement).children = this.normalizeSlateNodes(node.children, false);
        normalized.push(node);
      } else if (Text.isText(node)) {
        // merge adjacent text nodes with same formatting
        const lastNode = normalized[normalized.length - 1];
        if (Text.isText(lastNode) && this.hasSameFormatting(lastNode as CustomText, node as CustomText)) {
          (lastNode as CustomText).text += (node as CustomText).text;
        } else {
          normalized.push(node);
        }
      }
    }

    // remove empty nodes from the beginning and from the end
    if (root) {
      while (normalized.length > 0 && this.isEmptyNode(normalized[0])) {
        normalized.shift();
      }

      while (normalized.length > 0 && this.isEmptyNode(normalized[normalized.length - 1])) {
        normalized.pop();
      }
    }

    return normalized;
  }

  private isEmptyNode(node: Descendant): boolean {
    if (Element.isElement(node)) {
      // check if it's a paragraph with only empty text children
      if (node.type === 'paragraph') {
        return node.children.every((child) => Text.isText(child) && child.text.trim() === '');
      }
    }
    return false;
  }

  private hasSameFormatting(text1: CustomText, text2: CustomText): boolean {
    return (
      text1.bold === text2.bold &&
      text1.italic === text2.italic &&
      text1.underline === text2.underline &&
      text1.underlined === text2.underlined &&
      text1.strikethrough === text2.strikethrough &&
      text1.color === text2.color &&
      text1.fontSize === text2.fontSize
    );
  }
}
